/**
 * Palladium — Imported HTML Interactions
 *
 * GrapesJS strips <script> tags from imported HTML pages. This module
 * re-enables common interactive patterns found in professional landing
 * pages: scroll-spy, smooth anchor scroll, mobile nav toggle, and
 * sticky-header scroll class.
 *
 * Auto-initializes via MutationObserver when Blazor renders
 * .palladium-html-import containers.
 */
(function () {
    'use strict';

    // Track observers for cleanup
    var activeObservers = [];

    // ── Scroll-spy ──────────────────────────────────────────────────
    // Finds anchor links inside nav/aside containers that point to #id
    // targets on the page, and toggles .is-active based on scroll.
    function initScrollSpy(root) {
        var links = root.querySelectorAll('a[href^="#"]');
        if (links.length < 2 || !('IntersectionObserver' in window)) return;

        var pairs = [];
        links.forEach(function (link) {
            var id = link.getAttribute('href').substring(1);
            if (!id) return;
            var target = document.getElementById(id);
            if (target) pairs.push({ link: link, target: target, id: id });
        });
        if (pairs.length < 2) return;

        // Group by containing nav element
        var groups = [];
        var assigned = new Set();
        pairs.forEach(function (p) {
            if (assigned.has(p.link)) return;
            var nav = p.link.closest('nav, aside, [role="navigation"], .services-nav');
            if (!nav) return;
            var group = pairs.filter(function (q) { return nav.contains(q.link); });
            if (group.length < 2) return;
            group.forEach(function (q) { assigned.add(q.link); });
            groups.push(group);
        });

        groups.forEach(function (group) {
            var setActive = function (activeId) {
                group.forEach(function (p) {
                    p.link.classList.toggle('is-active', p.id === activeId);
                });
            };

            var io = new IntersectionObserver(function (entries) {
                var visible = entries.filter(function (e) { return e.isIntersecting; });
                if (visible.length) {
                    visible.sort(function (a, b) {
                        return a.boundingClientRect.top - b.boundingClientRect.top;
                    });
                    setActive(visible[0].target.id);
                }
            }, { rootMargin: '-30% 0px -55% 0px', threshold: 0 });

            group.forEach(function (p) { io.observe(p.target); });
            activeObservers.push(io);
        });
    }

    // ── Mobile nav toggle ───────────────────────────────────────────
    function initMobileNav(root) {
        root.querySelectorAll('[data-nav-toggle]').forEach(function (toggle) {
            if (toggle._pdInit) return;
            toggle._pdInit = true;

            var controlsId = toggle.getAttribute('aria-controls');
            var menu = controlsId
                ? document.getElementById(controlsId)
                : root.querySelector('[data-nav-mobile]');
            if (!menu) return;

            toggle.addEventListener('click', function () {
                var open = menu.classList.toggle('is-open');
                toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            });

            menu.querySelectorAll('a').forEach(function (a) {
                a.addEventListener('click', function () {
                    menu.classList.remove('is-open');
                    toggle.setAttribute('aria-expanded', 'false');
                });
            });
        });
    }

    // ── Header scroll class ─────────────────────────────────────────
    function initHeaderScroll(root) {
        var header = root.querySelector('.site-header')
            || document.querySelector('.site-header');
        if (!header || header._pdScrollInit) return;
        header._pdScrollInit = true;

        var update = function () {
            header.classList.toggle('is-scrolled', window.scrollY > 8);
        };
        window.addEventListener('scroll', update, { passive: true });
        update();
    }

    // ── Smooth anchor scroll ────────────────────────────────────────
    function initSmoothScroll(root) {
        root.querySelectorAll('a[href^="#"]').forEach(function (link) {
            if (link._pdSmoothInit) return;
            link._pdSmoothInit = true;

            link.addEventListener('click', function (e) {
                var id = link.getAttribute('href').substring(1);
                if (!id) return;
                var target = document.getElementById(id);
                if (target) {
                    e.preventDefault();
                    target.scrollIntoView({ behavior: 'smooth' });
                }
            });
        });
    }

    // ── Reveal on scroll ────────────────────────────────────────────
    // HtmlImport.razor already forces .reveal visible via CSS override.
    // This adds the .is-visible class so pages that define custom
    // reveal transitions can still use them.
    function initReveal(root) {
        var els = root.querySelectorAll('.reveal:not(.is-visible):not(.in)');
        if (!els.length) return;

        if ('IntersectionObserver' in window) {
            var io = new IntersectionObserver(function (entries) {
                entries.forEach(function (e) {
                    if (e.isIntersecting) {
                        e.target.classList.add('is-visible');
                        io.unobserve(e.target);
                    }
                });
            }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
            els.forEach(function (el) { io.observe(el); });
            activeObservers.push(io);
        } else {
            els.forEach(function (el) { el.classList.add('is-visible'); });
        }
    }

    // ── href on non-anchor elements ─────────────────────────────────
    // The visual editor sometimes exports a clickable element (icon button,
    // card…) as a <div href="/x"> instead of <a href="/x">. `href` is inert
    // on anything that isn't <a>/<area>, so the navigation silently fails.
    // Make any non-anchor element carrying an href behave like a link:
    // click + keyboard (Enter/Space), modifier/middle-click → new tab,
    // "#id" → smooth scroll. Idempotent (guarded per element).
    function initHrefNav(root) {
        var els = root.querySelectorAll('[href]:not(a):not(area):not(link):not(base):not(use)');
        els.forEach(function (el) {
            if (el._pdHrefNav) return;
            if (!el.getAttribute('href')) return;
            el._pdHrefNav = true;

            // Behave like a link for a11y if not already focusable/labelled.
            if (!el.hasAttribute('role')) el.setAttribute('role', 'link');
            if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
            if (!el.style.cursor) el.style.cursor = 'pointer';

            var go = function (e, newTab) {
                // Defer to a real interactive child (nested <a>/<button>).
                var inner = e.target.closest ? e.target.closest('a[href],button') : null;
                if (inner && inner !== el && el.contains(inner)) return;

                var h = el.getAttribute('href');
                if (!h) return;

                if (h.charAt(0) === '#') {
                    var t = document.getElementById(h.substring(1));
                    if (t) { e.preventDefault(); t.scrollIntoView({ behavior: 'smooth' }); }
                    return;
                }
                e.preventDefault();
                var tgt = el.getAttribute('target');
                if (newTab || tgt === '_blank') {
                    window.open(h, '_blank', 'noopener');
                } else {
                    window.location.assign(h);
                }
            };

            el.addEventListener('click', function (e) {
                go(e, e.metaKey || e.ctrlKey || e.button === 1);
            });
            el.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    go(e, false);
                }
            });
        });
    }

    // ── Logo → home ─────────────────────────────────────────────────
    // Single-page templates link the header logo to "#" (scroll to top).
    // On a multi-page site the logo must navigate home instead. Remap
    // before initHrefNav wires the element so go() reads the fixed href.
    function initLogoHome(root) {
        root.querySelectorAll('.header__logo[href="#"], .header__logo[href=""]')
            .forEach(function (el) { el.setAttribute('href', '/'); });
    }

    // ── FAQ accordion ───────────────────────────────────────────────
    // Imported pages ship .faq-item markup whose toggle <script> was
    // stripped on import; the page CSS drives visibility through the
    // `.faq-item.open` state (max-height transition on the answer).
    // The open height is set inline because editor-generated per-id
    // rules (#xyz{max-height:0}) outrank the .open class rule — and
    // scrollHeight avoids the fixed 300px clip on long answers.
    // Exclusive-open across the document, same as the original script.
    function initAccordions(root) {
        var setOpen = function (item, open) {
            item.classList.toggle('open', open);
            var btn = item.querySelector('.faq-item__btn');
            if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
            var ans = item.querySelector('.faq-item__answer');
            if (ans) ans.style.maxHeight = open ? ans.scrollHeight + 'px' : '';
        };

        root.querySelectorAll('.faq-item__btn').forEach(function (btn) {
            if (btn._pdFaqInit) return;
            btn._pdFaqInit = true;

            var item = btn.closest('.faq-item');
            if (!item) return;
            btn.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');

            btn.addEventListener('click', function () {
                var wasOpen = item.classList.contains('open');
                document.querySelectorAll('.faq-item.open').forEach(function (other) {
                    setOpen(other, false);
                });
                if (!wasOpen) setOpen(item, true);
            });
        });
    }

    // ── Chevron FAQ (tailwind-style imported templates) ─────────────
    // Pattern: item div with rounded-2xl + overflow-hidden containing a
    // <button> (question + empty icon div) followed by the answer <div>.
    // No CSS state exists for these — visibility is driven inline.
    // Closed by default, exclusive open within the item's list.
    function initChevronFaq(root) {
        root.querySelectorAll('button').forEach(function (btn) {
            if (btn._pdFaqInit) return;
            var item = btn.parentElement;
            if (!item || !/rounded-2xl/.test(item.className) || !/overflow-hidden/.test(item.className)) return;
            var ans = btn.nextElementSibling;
            if (!ans || ans.tagName !== 'DIV' || ans.querySelector('button')) return;
            btn._pdFaqInit = true;

            var icon = btn.querySelector('[class*="transition-transform"]');
            var setOpen = function (b, open) {
                var a = b.nextElementSibling;
                if (a && a.tagName === 'DIV') a.style.display = open ? '' : 'none';
                b.setAttribute('aria-expanded', open ? 'true' : 'false');
                var i = b.querySelector('[class*="transition-transform"]');
                if (i) i.style.transform = open ? 'rotate(45deg)' : 'rotate(0deg)';
            };

            if (icon && !icon.textContent.trim()) icon.textContent = '+';
            setOpen(btn, false);

            btn.addEventListener('click', function () {
                var wasOpen = ans.style.display !== 'none';
                var list = item.parentElement;
                (list ? list.querySelectorAll('button') : [btn]).forEach(function (b) {
                    if (b._pdFaqInit) setOpen(b, false);
                });
                if (!wasOpen) setOpen(btn, true);
            });
        });
    }

    // ── Year auto-fill ──────────────────────────────────────────────
    function initYear(root) {
        var el = root.querySelector('#year');
        if (el) el.textContent = new Date().getFullYear();
    }

    // ── In-view state classes ───────────────────────────────────────
    // The importer marks the elements whose CSS reacts to a class the site's own
    // script added once they scrolled into view (data-pb-inview="is-in" …): the
    // script is gone, add the class the same way.
    function initInView(root) {
        var els = root.querySelectorAll('[data-pb-inview]');
        if (!els.length) return;
        function show(el) {
            (el.getAttribute('data-pb-inview') || '').split(/\s+/).forEach(function (c) {
                if (c) el.classList.add(c);
            });
        }
        if (!('IntersectionObserver' in window)) { els.forEach(show); return; }
        var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
                if (e.isIntersecting) { show(e.target); io.unobserve(e.target); }
            });
        }, { threshold: 0.15 });
        els.forEach(function (el) {
            if (el._pdInView) return;
            el._pdInView = true;
            io.observe(el);
        });
        activeObservers.push(io);
    }

    // ── Main init ───────────────────────────────────────────────────
    function initContainer(container) {
        initInView(container);
        initReveal(container);
        initScrollSpy(container);
        initMobileNav(container);
        initHeaderScroll(container);
        initSmoothScroll(container);
        initLogoHome(container);
        initHrefNav(container);
        initAccordions(container);
        initChevronFaq(container);
        initYear(container);
    }

    // ── Auto-detect via MutationObserver ─────────────────────────────
    var debounce = null;

    function scheduleCheck() {
        clearTimeout(debounce);
        debounce = setTimeout(function () {
            // Wire <div href>/non-anchor links across the whole document FIRST and
            // in isolation — navigation is the critical behaviour. Previously a
            // throw inside any container's cosmetic init (scroll-spy/reveal/…) below
            // aborted the whole callback before initHrefNav ran, silently killing
            // every non-anchor link (e.g. the header "Compte" button).
            try { initLogoHome(document); } catch (e) { /* keep going */ }
            try { initHrefNav(document); } catch (e) { /* keep going */ }
            // Accordions document-wide too: Nodes-rendered .faq-item markup
            // (GenericNode) carries no .palladium-html-import class, so the
            // container pass below never sees it.
            try { initAccordions(document); } catch (e) { /* keep going */ }
            try { initChevronFaq(document); } catch (e) { /* keep going */ }

            document.querySelectorAll(
                '.palladium-html-import:not([data-pd-interactions])'
            ).forEach(function (el) {
                if (el.children.length === 0) return;
                el.setAttribute('data-pd-interactions', '1');
                try { initContainer(el); } catch (e) { /* one container must not break the rest */ }
            });
        }, 300);
    }

    if (document.body) {
        var mo = new MutationObserver(scheduleCheck);
        mo.observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scheduleCheck);
    } else {
        scheduleCheck();
    }

    window.__palladiumImportInteractions = { init: initContainer };
})();
